export const IMAGE_PIPELINE_VERSION = 1;

export const IMAGE_DERIVATIVE_SIZES = [
  "thumbnail",
  "960px",
  "2400px",
  "3600px",
] as const;

export type ImageDerivativeSize = (typeof IMAGE_DERIVATIVE_SIZES)[number];

export type ImageSourceKind = "sdr" | "gain-map-jpeg" | "pq-heif" | "hlg-heif";

const IMAGE_SOURCE_KINDS: ImageSourceKind[] = [
  "sdr",
  "gain-map-jpeg",
  "pq-heif",
  "hlg-heif",
];

export type ImageDerivative = {
  path: string;
  mediaType: string;
  width: number;
  height: number;
  hdr: boolean;
};

export type ImageDerivativeManifest = {
  pipelineVersion: number;
  sourceKind: ImageSourceKind;
  originalPath: string;
  derivatives: Partial<Record<ImageDerivativeSize, ImageDerivative>>;
};

export function getDerivativeFileName(
  sourceFileName: string,
  mediaType: string
) {
  const name = sourceFileName.split("/").pop() || sourceFileName;

  if (mediaType === "image/jpeg" && !/\.jpe?g$/i.test(name)) {
    return `${name}.jpg`;
  }

  return name;
}

export function getDerivativePath(
  userId: string,
  size: ImageDerivativeSize,
  sourceFileName: string,
  mediaType: string
) {
  return `${userId}/media/${size}/${getDerivativeFileName(
    sourceFileName,
    mediaType
  )}`;
}

export function isCurrentImageManifest(
  value: unknown
): value is ImageDerivativeManifest {
  if (!value || typeof value !== "object") return false;

  const manifest = value as Partial<ImageDerivativeManifest>;
  return (
    manifest.pipelineVersion === IMAGE_PIPELINE_VERSION &&
    typeof manifest.originalPath === "string" &&
    manifest.originalPath.length > 0 &&
    IMAGE_SOURCE_KINDS.includes(manifest.sourceKind as ImageSourceKind) &&
    !!manifest.derivatives &&
    typeof manifest.derivatives === "object" &&
    IMAGE_DERIVATIVE_SIZES.every((size) => {
      const derivative = manifest.derivatives?.[size];
      return (
        !derivative ||
        (typeof derivative.path === "string" &&
          derivative.path.length > 0 &&
          typeof derivative.mediaType === "string" &&
          derivative.mediaType.length > 0 &&
          Number.isFinite(derivative.width) &&
          derivative.width > 0 &&
          Number.isFinite(derivative.height) &&
          derivative.height > 0 &&
          typeof derivative.hdr === "boolean")
      );
    })
  );
}

export function hasCompleteImageDerivatives(
  value: unknown
): value is ImageDerivativeManifest {
  return (
    isCurrentImageManifest(value) &&
    IMAGE_DERIVATIVE_SIZES.every((size) => !!value.derivatives[size])
  );
}
